import {
  useEffect,
  useId,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';

import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * Combobox — the input-plus-listbox pattern (`FE-04.10`).
 *
 * `finding-things.md` calls it "the highest-leverage row in this table" and
 * claims it for **three** surfaces at once: the global search dropdown
 * (`FE-20.1`/`FE-20.2`), `FE-17.1`'s `@mention` autocomplete in the comment
 * composer — *another agent's surface* — and `FR-SEARCH-020`'s operator
 * typeahead in Phase 2. `card-back.md` defers mentions to B17 and asks for no
 * primitive, so there is no conflict, only a claim that crosses a boundary.
 * B3-BRIEF §5.2: honour it. "Building it once is the difference between one
 * accessible combobox and three near-misses."
 *
 * ── This is the only place the listbox focus model lives ───────────────────
 *
 * `role="combobox"` on the input, `role="listbox"` on the panel, **focus never
 * leaves the input**, and `aria-activedescendant` moves the active option.
 * `Popover` in this directory deliberately does not host this pattern — its
 * rows are a plain checkbox group with a tab stop each. Two different focus
 * models, and building one of them by accident is the failure B3-BRIEF §5.1
 * exists to prevent.
 *
 * `groups` renders `role="group"` + `aria-labelledby` headings, which is
 * `FR-SEARCH-015` AC1's "grouped results" and the reason a flat option list is
 * not enough. Arrow keys walk the **concatenation** of every group in order —
 * `finding-things.md` Decision 11: `searchResultsSchema` is independently
 * ordered and independently capped per group, so `cards[0]` is the best *card*
 * and not the best result, and the client composes the walk rather than
 * inventing a cross-group ranking.
 *
 * ── Not built here ────────────────────────────────────────────────────────
 * No fetching, no debounce, no minimum-length constant. `value` is the caller's
 * and `SEARCH_QUERY_MIN_LENGTH` is imported by the caller from
 * `@molt/shared`, never hardcoded a second time (`finding-things.md`
 * Decision 10). `minLength` here only suppresses options; it fires nothing.
 */

export interface ComboboxOption {
  /** Unique within the whole combobox, not just within its group. */
  id: string;
  /** The option's text. Used as its accessible name unless `renderOption` supplies richer content. */
  label: string;
}

export interface ComboboxGroup<T extends ComboboxOption = ComboboxOption> {
  id: string;
  /** The group heading. `FR-SEARCH-015` AC1 — "Cards", "Boards". */
  label: string;
  options: T[];
}

export interface ComboboxProps<T extends ComboboxOption = ComboboxOption> extends Omit<
  ComponentPropsWithoutRef<'input'>,
  | 'value'
  | 'onChange'
  | 'role'
  | 'aria-expanded'
  | 'aria-controls'
  | 'aria-activedescendant'
  | 'onSubmit'
> {
  /**
   * The accessible name of the input. Rendered as a real `<label>`; set
   * `hideLabel` to keep it in the accessibility tree only. Never a
   * placeholder-only field — B3-BRIEF §4's rule for `TextField` applies here
   * for the same reason.
   */
  label: string;
  hideLabel?: boolean;
  value: string;
  onChange: (value: string) => void;
  groups: ComboboxGroup<T>[];
  /** Enter on the active option, or a click on any option. */
  onActivate: (option: T) => void;
  /** Enter with no active option. Used by full-search fall-through. */
  onSubmit?: (value: string) => void;
  /**
   * Marks the panel busy. A spinner inside a `role="listbox"` would be an
   * option with no value and would break `aria-activedescendant`
   * (`finding-things.md`), so this announces through a live region and the
   * caller renders `Skeleton` rows in `emptyState` if it wants shape.
   */
  loading?: boolean;
  /** Announced politely while `loading`. */
  loadingLabel?: string;
  /**
   * Shown in the open panel when there are no options to show — "no matches",
   * "keep typing", "search is unavailable". One slot, because the caller is the
   * only thing that knows which of those it is (`FR-SEARCH-015` AC4: "nothing
   * found" and "the request failed" must not be drawn the same way).
   */
  emptyState?: ReactNode;
  /** Below this many characters no options render, whatever `groups` contains. */
  minLength?: number;
  /** Opens the panel on focus with an empty value. Used by global search jump-to. */
  openWhenEmpty?: boolean;
  /** Renders the body of an option row. Defaults to `option.label`. */
  renderOption?: (option: T) => ReactNode;
  /** Rendered inside the field, before the input. */
  leadingIcon?: ReactNode;
  /** Rendered below the listbox while the panel is open. */
  footer?: ReactNode;
  /**
   * Renders the listbox in flow, under the input, and always open — for a
   * combobox that is the whole body of a surface that is itself the overlay
   * (`BoardSwitcher` inside a `Popover`). The default absolutely positioned
   * panel would be clipped there: `Popover`'s content is `overflow-hidden` and
   * an absolute child adds no height to the body that scrolls it.
   */
  inline?: boolean;
  className?: string;
}

export function Combobox<T extends ComboboxOption = ComboboxOption>({
  label,
  hideLabel = false,
  value,
  onChange,
  groups,
  onActivate,
  onSubmit,
  loading = false,
  loadingLabel,
  emptyState,
  minLength = 0,
  openWhenEmpty = false,
  renderOption,
  leadingIcon,
  footer,
  inline = false,
  className,
  onKeyDown,
  onFocus,
  onBlur,
  ...rest
}: ComboboxProps<T>) {
  const { t } = useInterfaceLanguage();
  const baseId = useId();
  const inputId = `${baseId}-input`;
  const listboxId = `${baseId}-listbox`;

  const [expanded, setExpanded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const trimmedLength = value.trim().length;
  const optionsAllowed =
    trimmedLength >= minLength || (openWhenEmpty && trimmedLength === 0);
  // The concatenation the keyboard walks. Order is the caller's group order.
  const flat = optionsAllowed ? groups.flatMap((group) => group.options) : [];

  // Clamped rather than reset, so a parent that rebuilds `groups` on every
  // render does not silently drop the member's arrow position.
  const active = activeIndex >= 0 && activeIndex < flat.length ? activeIndex : -1;
  const activeOption = active >= 0 ? flat[active] : undefined;
  const activeOptionId = activeOption ? `${listboxId}-opt-${activeOption.id}` : undefined;

  // A new query invalidates the position; nothing else does.
  useEffect(() => {
    setActiveIndex(-1);
  }, [value]);

  const open = inline || (expanded && (value.length > 0 || openWhenEmpty));
  const showEmptyState = open && flat.length === 0 && emptyState !== undefined;

  // `inline` only: keeps the active option in view once the list outgrows its
  // panel. The overlay callers keep their shipped behaviour. Optional-called:
  // jsdom implements no `scrollIntoView`.
  useEffect(() => {
    if (!inline || activeOptionId === undefined) return;
    document.getElementById(activeOptionId)?.scrollIntoView?.({ block: 'nearest' });
  }, [inline, activeOptionId]);

  const move = (delta: number) => {
    if (flat.length === 0) return;
    setActiveIndex((current) => {
      const from =
        current >= 0 && current < flat.length ? current : delta > 0 ? -1 : flat.length;
      return (from + delta + flat.length) % flat.length;
    });
  };

  return (
    <div className={cn('relative', className)}>
      <label
        htmlFor={inputId}
        className={cn(hideLabel ? 'sr-only' : 'mb-1 block text-sm font-medium text-ink')}
      >
        {label}
      </label>

      <div className="flex items-center gap-2 rounded-card border border-border-strong bg-surface-raised px-2.5 py-1.5 focus-within:border-accent">
        {leadingIcon ? (
          <span aria-hidden="true" className="shrink-0 text-ink-muted">
            {leadingIcon}
          </span>
        ) : null}
        <input
          {...rest}
          id={inputId}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          value={value}
          // No `outline-none`. `styles.css` sets the `:focus-visible` ring
          // globally and B3-BRIEF §3 forbids removing it; the wrapper's accent
          // edge is in addition to the ring, never instead of it.
          className="min-w-0 flex-1 bg-transparent text-card text-ink placeholder:text-ink-muted"
          onChange={(event) => {
            setExpanded(true);
            onChange(event.target.value);
          }}
          onFocus={(event) => {
            if (value.length > 0 || openWhenEmpty) setExpanded(true);
            onFocus?.(event);
          }}
          onBlur={(event) => {
            setExpanded(false);
            onBlur?.(event);
          }}
          onKeyDown={(event) => {
            onKeyDown?.(event);
            if (event.defaultPrevented) return;
            switch (event.key) {
              case 'ArrowDown':
                event.preventDefault();
                setExpanded(true);
                move(1);
                break;
              case 'ArrowUp':
                event.preventDefault();
                setExpanded(true);
                move(-1);
                break;
              case 'Home':
                if (!open) break;
                event.preventDefault();
                setActiveIndex(flat.length > 0 ? 0 : -1);
                break;
              case 'End':
                if (!open) break;
                event.preventDefault();
                setActiveIndex(flat.length - 1);
                break;
              case 'Enter': {
                // Nothing active means nothing to activate: Enter belongs to
                // the caller's submit path, not to an option.
                if (!activeOption) {
                  if (onSubmit) {
                    event.preventDefault();
                    onSubmit(value);
                    setExpanded(false);
                  }
                  break;
                }
                event.preventDefault();
                onActivate(activeOption);
                setExpanded(false);
                break;
              }
              case 'Escape':
                if (!open) break;
                // Closes; the query stays. `FR-SEARCH-015` AC5.
                event.preventDefault();
                setExpanded(false);
                break;
              default:
                break;
            }
          }}
        />
      </div>

      {open ? (
        <div
          // Keeps focus in the input when an option is clicked: `mousedown`
          // would blur the combobox and close the panel before `click` lands.
          onMouseDown={(event) => event.preventDefault()}
          className={
            inline
              ? 'mt-2 max-h-[min(50vh,22rem)] overflow-y-auto'
              : 'absolute z-50 mt-1 max-h-[min(60vh,24rem)] w-full overflow-y-auto rounded-card border border-border-strong bg-surface-raised shadow-card'
          }
        >
          <div
            role="listbox"
            id={listboxId}
            aria-label={label}
            aria-busy={loading || undefined}
          >
            {groups.map((group) => {
              const headingId = `${listboxId}-grp-${group.id}`;
              const options = optionsAllowed ? group.options : [];
              if (options.length === 0) return null;
              return (
                <div key={group.id} role="group" aria-labelledby={headingId}>
                  <div
                    id={headingId}
                    // `--color-ink-muted` is legal here and only here-ish: the
                    // panel is opaque `--color-surface-raised` (4.93:1
                    // measured), not the translucent `--color-panel` whose 92%
                    // floor forbids muted text.
                    className="px-3 pt-2 pb-1 text-xs font-medium tracking-wide text-ink-muted uppercase"
                  >
                    {group.label}
                  </div>
                  {options.map((option) => {
                    const index = flat.indexOf(option);
                    const isActive = index === active;
                    return (
                      <div
                        key={option.id}
                        id={`${listboxId}-opt-${option.id}`}
                        role="option"
                        aria-selected={isActive}
                        className={cn(
                          'cursor-pointer px-3 py-2 text-card text-ink',
                          isActive && 'bg-accent text-accent-ink',
                        )}
                        onClick={() => {
                          setActiveIndex(index);
                          onActivate(option);
                          setExpanded(false);
                        }}
                      >
                        {renderOption ? renderOption(option) : option.label}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          {showEmptyState ? <div className="px-3 py-3">{emptyState}</div> : null}
          {footer ? (
            <div className="border-t border-border-strong px-3 py-2">{footer}</div>
          ) : null}
        </div>
      ) : null}

      {/*
        The busy signal is a live region rather than a spinner in the list, so
        `aria-activedescendant` keeps pointing at a real option throughout.
      */}
      <span role="status" aria-live="polite" className="sr-only">
        {loading ? (loadingLabel ?? t('primitives.combobox.searching')) : ''}
      </span>
    </div>
  );
}
